import { useContext, useEffect } from 'react'
import { AuthContext } from '@/context/auth-context'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { kIsExternalStorageProviderEnabled, type ExternalStorageProvider } from '@/constants/constants-external-storage'
import { PROVIDER_ICON_COMPONENTS } from '@/user_files/external-storage/ProviderMeta'
import { selectActiveMatterId } from '@/matter/store/selectors'
import {
  ExternalStorageActions,
  kExternalStorageRequestStatus,
  selectExternalStorageAccountsRequest,
  selectVisibleConnectedExternalAccount,
} from '@/user_files/external-storage/store/slice'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { MatterDriveSyncEntrySource } from '@/analytics/enums/matter-drive-sync.enums'
import { getDriveSyncOrganizationId, trackMatterDriveSyncEvent } from '@/analytics/utils/matter-drive-sync-analytics'
import { SettingsEntrySource } from '@/analytics/enums/settings-entry.enums'
import { useOpenConnectedAccountsSettings } from '@/settings-modal/hooks/useOpenConnectedAccountsSettings'

const PREVIEW_PROVIDERS: ExternalStorageProvider[] = ['box', 'dropbox', 'google_drive', 'onedrive']

export default function SidebarSyncFilesCard() {
  const dispatch = useAppDispatch()
  const openConnectedAccountsSettings = useOpenConnectedAccountsSettings()
  const { user, userAccountData } = useContext(AuthContext)
  const { trackEvent } = useAnalytics()
  const driveSyncEnabled = kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC)
  const shouldLoadAccounts = driveSyncEnabled && !user?.isAnonymous
  const connectedAccount = useAppSelector(selectVisibleConnectedExternalAccount)
  const accountsRequest = useAppSelector(selectExternalStorageAccountsRequest)
  const activeMatterId = useAppSelector(selectActiveMatterId)
  const organizationId = getDriveSyncOrganizationId(userAccountData)

  useEffect(() => {
    if (shouldLoadAccounts && accountsRequest.status === kExternalStorageRequestStatus.IDLE) {
      dispatch(ExternalStorageActions.fetchConnectedAccounts())
    }
  }, [accountsRequest.status, dispatch, shouldLoadAccounts])

  const loading = accountsRequest.status === kExternalStorageRequestStatus.IDLE || accountsRequest.status === kExternalStorageRequestStatus.LOADING

  // Only prompt to connect a DMS when drive sync is enabled, we know no account is connected, and
  // the user is on the global sidebar (no matter selected). Stay hidden while the initial fetch is
  // in flight so an already-connected user never sees the prompt flash before their account loads.
  if (!shouldLoadAccounts || connectedAccount || loading || activeMatterId !== null) {
    return null
  }

  // Pre-connection discovery — Settings link. No matterId: the card is not matter-scoped.
  const handleSettingsClick = () => {
    trackMatterDriveSyncEvent(
      AnalyticsEvent.MatterDriveSyncDiscoveryClicked,
      {
        organizationId,
        matterDriveSyncEntrySource: MatterDriveSyncEntrySource.Sidebar,
      },
      trackEvent
    )
    openConnectedAccountsSettings(SettingsEntrySource.SidebarSyncFiles)
  }

  return (
    <div className="px-3 py-3">
      <div className="flex flex-col gap-2 rounded-lg border border-brand-200 bg-brand-50 px-6 py-4">
        <p className="font-rethink-sans text-base font-medium leading-6 text-text-primary">Sync your files</p>
        <p className="text-sm leading-5 tracking-[-0.15px] text-text-tertiary">
          Connect a file management system in{' '}
          <button type="button" className="text-brand-800 underline" onClick={handleSettingsClick}>
            settings
          </button>
          .
        </p>
        <div className="flex items-center gap-3 opacity-60">
          {PREVIEW_PROVIDERS.filter(kIsExternalStorageProviderEnabled).map((provider) => {
            const ProviderIcon = PROVIDER_ICON_COMPONENTS[provider]
            return <ProviderIcon key={provider} className="h-[18px] w-5 rounded-md" aria-hidden />
          })}
        </div>
      </div>
    </div>
  )
}
